import type { Subscription, SubscriptionState } from '../../types/subscription';

export function formatDate(dateString: string, locale: string = 'en-US'): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

export function formatShortDate(dateString: string, locale: string = 'en-US'): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

export function getCardBrandIcon(brand: string): string {
  const brandLower = brand.toLowerCase();

  const icons: Record<string, string> = {
    visa: '💳',
    mastercard: '💳',
    amex: '💳',
    discover: '💳',
    diners: '💳',
    elo: '💳',
    hipercard: '💳',
  };

  return icons[brandLower] || '💳';
}

export function formatCardNumber(number: string): string {
  const cleaned = number.replace(/\s/g, '');
  const groups = cleaned.match(/.{1,4}/g) || [];
  return groups.join(' ');
}

export function validateCardNumber(number: string): boolean {
  const cleaned = number.replace(/\s/g, '');

  if (!/^\d{13,19}$/.test(cleaned)) {
    return false;
  }

  let sum = 0;
  let isEven = false;

  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
}

export function validateCVV(cvv: string): boolean {
  return /^\d{3,4}$/.test(cvv);
}

export function validateExpiry(expiry: string): boolean {
  const match = expiry.match(/^(\d{2})\/(\d{2,4})$/);
  if (!match) {
    return false;
  }

  const month = parseInt(match[1], 10);
  let year = parseInt(match[2], 10);

  if (month < 1 || month > 12) {
    return false;
  }

  if (year < 100) {
    year += 2000;
  }

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  if (year < currentYear || (year === currentYear && month < currentMonth)) {
    return false;
  }

  return true;
}

export function canReactivate(subscription: Subscription): boolean {
  if (!subscription.canceledAt || subscription.status !== 'ACTIVE') {
    return false;
  }

  const now = new Date();
  const periodEnd = new Date(subscription.currentPeriodEnd);

  return now < periodEnd;
}

export function getSubscriptionState(subscription: Subscription): SubscriptionState {
  const isTrialing = subscription.status === 'TRIALING';
  const isCanceled = subscription.canceledAt !== null;
  const canReact = canReactivate(subscription);
  const isExpired = subscription.status === 'CANCELED' || subscription.status === 'EXPIRED';
  const isActive = subscription.status === 'ACTIVE' && !isCanceled;

  let state: SubscriptionState['state'];

  if (isTrialing) {
    state = 'trialing';
  } else if (isCanceled && canReact) {
    state = 'canceled_can_reactivate';
  } else if (isExpired || (isCanceled && !canReact)) {
    state = 'expired';
  } else {
    state = 'active';
  }

  return {
    state,
    canReactivate: canReact,
    canUpgrade: isActive || isTrialing || (isCanceled && canReact),
    canDowngrade: isActive || isTrialing || (isCanceled && canReact),
    canCancel: isActive || isTrialing,
  };
}

export function calculateProration(
  currentAmount: number,
  newAmount: number,
  daysRemaining: number,
  totalDays: number
): number {
  const unusedCredit = Math.floor((currentAmount * daysRemaining) / totalDays);
  const newCharge = Math.floor((newAmount * daysRemaining) / totalDays);

  return newCharge - unusedCredit;
}

export function getDaysRemaining(periodEnd: string): number {
  const now = new Date();
  const end = new Date(periodEnd);
  const diff = end.getTime() - now.getTime();

  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}
